// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import moment from 'moment';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseDynamicSelect,
  BaseLabel,
  BaseSelect,
  BaseTable,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBaseOption, IHeaderCellType, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { DateFormatEnum, DateTypeReportEnum, ReportTypeEnum, TypeCrmEnum } from '~/utils/enum/common';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import { ICancellationReport, IQueryParamListExport } from '~/utils/interface/reports';
import { exportDataReport, getListCancellationReport } from '~/thunks/reports/reportsThunk';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  EMPTY_VALUE,
  optionDateType,
} from '~/utils/constants/common';
import { IClient, IOptionClient, IQueryParamClient } from '~/utils/interface/client';
import { getListClient } from '~/thunks/client/clientThunk';
// Styles, images, icons
import styles from './CancellationReport.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<ICancellationReport>[] => {
  return [
    {
      id: 'orderNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_table_quote_number_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => (
        <Stack>
          <Typography className={cx('orderNumber')}>{record?.quoteNumber}</Typography>
        </Stack>
      ),
    },
    {
      id: 'title',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_table_total_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return formatCurrencyUSD({ value: record?.total, showDecimal: true });
      },
    },
    {
      id: 'amountPaid',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_table_amount_paid_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return formatCurrencyUSD({ value: record?.amountPaid, showDecimal: true });
      },
    },
    {
      id: 'eventType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_table_event_type_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return <Box className={cx('typeView')}>{record?.eventType.name}</Box>;
      },
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_table_client_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return getFullName({ firstName: record?.client?.firstName, lastName: record?.client?.lastName });
      },
    },
    {
      id: 'manager',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_managed_by')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return getFullName({ firstName: record?.managedBy?.firstName, lastName: record?.managedBy?.lastName });
      },
    },
    {
      id: 'dateCanceled',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_date_canceled_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return formatDate(record?.cancelledAt, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE;
      },
    },
    {
      id: 'externalNote',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_table_note_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return record?.externalNote || EMPTY_VALUE;
      },
    },
    {
      id: 'assignedUser',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_cancellations_sale_person_title')}</Box>
        </Stack>
      ),
      render: (record: ICancellationReport) => {
        return (
          getFullName({ firstName: record?.assignedUser?.firstName, lastName: record?.assignedUser?.lastName }) ||
          EMPTY_VALUE
        );
      },
    },
  ];
};

const CancellationReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [cancellationList, setCancellationList] = useState<ICancellationReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    dateType: DateTypeReportEnum.CANCELLATION,
    startDate: moment().format(DateFormatEnum.YYYY_MM_DD),
    endDate: moment().format(DateFormatEnum.YYYY_MM_DD),
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListCancellation();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListCancellation = () => {
    setIsLoading(true);

    dispatch(getListCancellationReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setCancellationList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListCancellation();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.CANCELLATION,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionsClient = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionClient>> => {
      const { searchKey, page } = payload;
      const payloadParams: IQueryParamClient = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        type: TypeCrmEnum.CLIENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListClient(payloadParams)).unwrap();

        const { responses: listClient, pagination } = resp?.data;

        const formatOptions: IOptionClient[] = listClient?.map((item: IClient) => ({
          value: item?.id,
          label:
            item?.firstName || item?.lastName
              ? getFullName({ firstName: item?.firstName, lastName: item?.lastName })
              : item?.entityName,
          email: item?.clientContacts?.[0]?.email || EMPTY_STRING,
        }));

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const fetchOptionDateType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionDateType,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Stack id='reportsCancellationPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_cancellations_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_cancellations_desc')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_date_type')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  dateType: data?.value,
                }));
              }}
              callApiFn={fetchOptionDateType}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDynamicSelect
              multiple={true}
              label={t('reports_filter_client')}
              callApiFn={fetchOptionsClient}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  clientId: data?.idSelected,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={cancellationList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default CancellationReport;
